import { gsap } from 'gsap';

const escape = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Wraps each word so it can be staggered without touching the parent's fragment opacity. */
export function words(text: string): string {
  return text
    .split(' ')
    .map((word) => `<span class="word"><span class="word__inner">${escape(word)}</span></span>`)
    .join(' ');
}

/** Wraps each character; used for the wordmark. */
export function chars(text: string): string {
  return Array.from(text)
    .map((char) => `<span class="char">${char === ' ' ? '&nbsp;' : escape(char)}</span>`)
    .join('');
}

/** Standard statement entrance: words rise out of a soft blur. */
export function revealWords(
  container: Element,
  { delay = 0, stagger = 0.07, duration = 0.8 }: { delay?: number; stagger?: number; duration?: number } = {},
): gsap.core.Tween {
  return gsap.fromTo(
    container.querySelectorAll('.word__inner'),
    { yPercent: 55, opacity: 0, filter: 'blur(10px)' },
    { yPercent: 0, opacity: 1, filter: 'blur(0px)', duration, delay, stagger, ease: 'power4.out', overwrite: true },
  );
}

/** Deterministic end state for statements when navigating instantly. */
export function settleWords(container: Element): void {
  gsap.set(container.querySelectorAll('.word__inner'), { yPercent: 0, opacity: 1, filter: 'blur(0px)' });
}

/** Counts a number up (or down) inside an element, formatted with thousands separators. */
export function countTo(
  element: Element,
  to: number,
  { from, duration = 1.4, decimals = 0, suffix = '', immediate = false }: { from?: number; duration?: number; decimals?: number; suffix?: string; immediate?: boolean } = {},
): gsap.core.Tween {
  const format = (value: number): string =>
    value.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
  const start = from ?? Number(element.getAttribute('data-value') ?? 0);
  const proxy = { value: start };
  element.setAttribute('data-value', String(to));
  if (immediate) {
    element.textContent = format(to);
    return gsap.set(proxy, { value: to });
  }
  return gsap.to(proxy, {
    value: to,
    duration,
    ease: 'power2.out',
    onUpdate: () => { element.textContent = format(proxy.value); },
  });
}
